import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import { Inventory2Outlined, WarningAmberOutlined, RemoveShoppingCartOutlined, CheckCircleOutline } from "@mui/icons-material";
import { SparePageHeader, SpareSummary, sparePageSx } from "../../components/SparePagePresentation";

import {
  AppDeleteDialog,
  AppSearchField,
  AppSnackbar,
  AppToolbar,
} from "../../../../components/common";

import InventoryFormDialog from "../components/InventoryFormDialog";
import InventoryDetailsDialog from "../components/InventoryDetailsDialog";
import InventoryTable from "../components/InventoryTable";
import InventoryService from "../api/inventory.service";

import { useInventory } from "../hooks/useInventory";
import { filterRowsBySearch } from "../../utils/rowSearch";

import {
  Inventory,
  InventoryPayload,
  SelectOption,
} from "../types";

type InventoryTab = "all" | "lowStock" | "outOfStock" | "healthy";

const inventoryTabs: Array<{
  value: InventoryTab;
  label: string;
  description: string;
}> = [
  {
    value: "all",
    label: "All Spares",
    description: "Complete hub-wise spare stock availability.",
  },
  {
    value: "lowStock",
    label: "Low Stock",
    description: "Spares at or below reorder level.",
  },
  {
    value: "outOfStock",
    label: "Out Of Stock",
    description: "Spares currently unavailable in the selected hub.",
  },
  {
    value: "healthy",
    label: "Healthy Stock",
    description: "Spares above reorder level and ready to issue.",
  },
];

const InventoryPage = () => {
  const {
    inventory,
    loading,
    error,
    pagination,
    filters,
    createInventory,
    updateInventory,
    deleteInventory,
    updateStatus,
    updateFilters,
  } = useInventory();

  const [selectedInventory, setSelectedInventory] =
    useState<Inventory | null>(null);

  const [formOpen, setFormOpen] =
    useState(false);

  const [deleteOpen, setDeleteOpen] =
    useState(false);

  const [detailsOpen, setDetailsOpen] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [snackbar, setSnackbar] =
    useState({
      open: false,
      severity: "success" as
        | "success"
        | "error"
        | "warning"
        | "info",
      message: "",
    });

  const [spareParts, setSpareParts] = useState<SelectOption[]>([]);
  const [warehouses, setWarehouses] = useState<SelectOption[]>([]);
  const [activeTab, setActiveTab] = useState<InventoryTab>("all");

  const inventoryStats = {
    all: inventory.length,
    lowStock: inventory.filter(
      (item) => item.availableStock > 0 && item.availableStock <= item.reorderLevel
    ).length,
    outOfStock: inventory.filter((item) => item.availableStock === 0).length,
    healthy: inventory.filter((item) => item.availableStock > item.reorderLevel).length,
  };

  const visibleInventory = inventory.filter((item) => {
    if (activeTab === "lowStock") {
      return item.availableStock > 0 && item.availableStock <= item.reorderLevel;
    }

    if (activeTab === "outOfStock") {
      return item.availableStock === 0;
    }

    if (activeTab === "healthy") {
      return item.availableStock > item.reorderLevel;
    }

    return true;
  });

  const warehouseNameById = new Map(
    warehouses.map((warehouse) => [warehouse.id, warehouse.name])
  );

  const visibleInventoryRows = filterRowsBySearch(
    visibleInventory.map((item) => ({
    ...item,
    warehouseName:
      warehouseNameById.get(item.warehouseId) || item.warehouseName || item.warehouseId,
    })),
    filters.search
  );

  const activeInventoryTab =
    inventoryTabs.find((tab) => tab.value === activeTab) || inventoryTabs[0];

  useEffect(() => {
    InventoryService.getSparePartOptions()
      .then(setSpareParts)
      .catch(() => setSpareParts([]));

    InventoryService.getHubOptions()
      .then(setWarehouses)
      .catch(() => setWarehouses([]));
  }, []);

  const handleCreate = () => {
    setSelectedInventory(null);

    setFormOpen(true);
  };

  const handleEdit = (
    item: Inventory
  ) => {
    setSelectedInventory(item);

    setFormOpen(true);
  };

  const handleDelete = (
    item: Inventory
  ) => {
    setSelectedInventory(item);

    setDeleteOpen(true);
  };

  const handleHistory = (
    item: Inventory
  ) => {
    console.log(item);
  };

  const handleSearch = (
    value: string
  ) => {
    updateFilters({
      search: value,
      page: 1,
    });
  };

  const handlePageChange = (
    page: number
  ) => {
    updateFilters({
      page: page + 1,
    });
  };

  const handleView = (
    item: Inventory
  ) => {
    setSelectedInventory(item);
    setDetailsOpen(true);
  };

  const handlePageSizeChange = (
    pageSize: number
  ) => {
    updateFilters({
      limit: pageSize,
      page: 1,
    });
  };

  const handleStatusChange =
    async (
      item: Inventory,
      checked: boolean
    ) => {
      try {
        await updateStatus(
          item.id,
          checked
            ? "ACTIVE"
            : "INACTIVE"
        );

        setSnackbar({
          open: true,
          severity: "success",
          message:
            "Status updated successfully.",
        });
      } catch {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            "Failed to update status.",
        });
      }
    };

  const handleSaveInventory =
    async (
      payload: InventoryPayload
    ) => {
      try {
        setSaving(true);

        if (selectedInventory) {
          await updateInventory(
            selectedInventory.id,
            payload
          );

          setSnackbar({
            open: true,
            severity: "success",
            message:
              "Inventory updated successfully.",
          });
        } else {
          await createInventory(
            payload
          );

          setSnackbar({
            open: true,
            severity: "success",
            message:
              "Inventory created successfully.",
          });
        }

        setFormOpen(false);

        setSelectedInventory(null);
      } catch {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            "Unable to save inventory.",
        });
      } finally {
        setSaving(false);
      }
    };

  return (
    <Box
      sx={sparePageSx("inventory")}
    >
      <SparePageHeader kind="inventory"
        title="Inventory Management"
        subtitle="All spare availability, low stock alerts, and stock movement controls."
        action={<Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>Add Stock</Button>}
      />

      <SpareSummary items={[
        { label: "All Spares · this page", value: loading ? "—" : inventoryStats.all, icon: <Inventory2Outlined />, color: "#0284c7" },
        { label: "Low Stock · this page", value: loading ? "—" : inventoryStats.lowStock, icon: <WarningAmberOutlined />, color: "#d97706" },
        { label: "Out of Stock · this page", value: loading ? "—" : inventoryStats.outOfStock, icon: <RemoveShoppingCartOutlined />, color: "#e11d48" },
        { label: "Healthy · this page", value: loading ? "—" : inventoryStats.healthy, icon: <CheckCircleOutline />, color: "#10b981" },
      ]} />

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value: InventoryTab) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: { xs: 1, md: 2 },
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontWeight: 800,
              borderRadius: 2,
              my: 0.75,
              minWidth: { xs: 120, md: 142 },
            },
          }}
        >
          {inventoryTabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={`${tab.label} (${inventoryStats[tab.value]})`}
            />
          ))}
        </Tabs>

        <Stack sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
          <Typography variant="caption" color="text.secondary">
            {activeInventoryTab.description}
          </Typography>
        </Stack>
      </Paper>

      <AppToolbar
        left={
          <AppSearchField
            value={filters.search ?? ""}
            onChange={handleSearch}
            placeholder="Search Inventory..."
          />
        }
      />
          <InventoryTable
        rows={visibleInventoryRows}
        loading={loading}
        page={(filters.page || 1) - 1}
        pageSize={filters.limit || 10}
        rowCount={activeTab === "all" ? pagination.total : visibleInventory.length}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        onHistory={handleHistory}
        onStatusChange={handleStatusChange}
      />

      <InventoryFormDialog
        open={formOpen}
        loading={saving}
        inventory={selectedInventory}
        spareParts={spareParts}
        warehouses={warehouses}
        onClose={() => {
          setFormOpen(false);
          setSelectedInventory(null);
        }}
        onSubmit={handleSaveInventory}
      />

      <InventoryDetailsDialog
        open={detailsOpen}
        inventory={selectedInventory}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedInventory(null);
        }}
      />

      <AppDeleteDialog
        open={deleteOpen}
        title="Delete Inventory"
        content={`Are you sure you want to delete "${
          selectedInventory?.sparePartName ?? ""
        }"? This action cannot be undone.`}
        loading={saving}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedInventory(null);
        }}
        onConfirm={async () => {
          if (!selectedInventory) return;

          try {
            setSaving(true);

            await deleteInventory(
              selectedInventory.id
            );

            setDeleteOpen(false);

            setSelectedInventory(null);

            setSnackbar({
              open: true,
              severity: "success",
              message:
                "Inventory deleted successfully.",
            });
          } catch {
            setSnackbar({
              open: true,
              severity: "error",
              message:
                "Failed to delete inventory.",
            });
          } finally {
            setSaving(false);
          }
        }}
      />
            <AppSnackbar
        open={snackbar.open}
        severity={snackbar.severity}
        message={snackbar.message}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
      />

      {error && (
        <AppSnackbar
          open={Boolean(error)}
          severity="error"
          message={error}
          onClose={() => {}}
        />
      )}
    </Box>
  );
};


export default InventoryPage;
